import type { CSSProperties } from 'react';

import { LoadingDots } from './LoadingDots';
import styles from './MicButtonPlaceholder.module.css';

/**
 * Перенос `lib/src/presentation/widgets/mic_button_placeholder.dart`.
 *
 * Кольцо рисуется SVG-эллипсом, а не рамкой: во Flutter это дуга по овалу,
 * вписанному в бокс, и только эллипс повторяет её и на неквадратном
 * размере. Вызов в приложении один и квадратный — `Size(200.w, 200.w)`.
 */

/** `strokeWidth: 6` */
const STROKE_WIDTH = 6;

/** `strokeAlign: -0.5` */
const STROKE_ALIGN = -0.5;

/**
 * Отступ овала от краёв бокса: во Flutter это `-strokeAlign * strokeWidth / 2`.
 * При -0.5 обводка на четверть толщины уходит наружу бокса.
 */
const STROKE_OFFSET = (-STROKE_ALIGN * STROKE_WIDTH) / 2;

/** Нормировка пунктира: вся окружность — 100 единиц. */
const ARC_PATH_LENGTH = 100;

export interface MicButtonPlaceholderSize {
  /** Уже посчитанные пиксели (в Dart сюда приходит `Size(200.w, 200.w)`). */
  readonly width: number;
  readonly height: number;
}

export interface MicButtonPlaceholderProps {
  readonly size: MicButtonPlaceholderSize;
}

function ProgressRing({ width, height }: MicButtonPlaceholderSize) {
  return (
    <div className={styles.ring}>
      <svg className={styles.ringSvg} viewBox={`0 0 ${width} ${height}`}>
        <ellipse
          className={styles.ringArc}
          cx={width / 2}
          cy={height / 2}
          rx={(width - STROKE_OFFSET * 2) / 2}
          ry={(height - STROKE_OFFSET * 2) / 2}
          pathLength={ARC_PATH_LENGTH}
        />
      </svg>
    </div>
  );
}

export function MicButtonPlaceholder({ size }: MicButtonPlaceholderProps) {
  const style: CSSProperties = {
    width: `${size.width}px`,
    height: `${size.height}px`,
  };
  return (
    <div className={styles.root} style={style}>
      <div className={styles.circle}>
        <LoadingDots />
      </div>
      <ProgressRing width={size.width} height={size.height} />
    </div>
  );
}
